import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { MainContent } from '@/components/layout/MainContent';
import { JoinQueue } from '@/components/pages/JoinQueue';
import { LiveDashboard } from '@/components/pages/LiveDashboard';
import { ImpersonationBar } from '@/components/ImpersonationBar';
import { useAuth } from '@/hooks/useApi';
import { cognitoAuth } from '@/lib/cognito';
import { loadTenantCognitoConfig, isTenantConfigLoaded } from '@/config/cognito';
import { KPIData } from '@/types';
import { TenantProvider, useTenantContext } from '@/contexts/TenantContext';
import { ThemeProvider } from '@/contexts/ThemeContext';


// Mock data - replace with real API calls
const mockKPIData: KPIData = {
  avgWait: 12,
  peak: 25,
  offPeak: 8,
  samples: 1247
};

function getSubdomainTenant(): string | null {
  const hostname = window.location.hostname
  
  // Skip admin and api subdomains for all environments
  if (hostname === 'admin.queuemein.com' || hostname === 'api.queuemein.com' || hostname === 'queuemein.com' ||
      hostname === 'admin.dev.queuemein.com' || hostname === 'api.dev.queuemein.com' || hostname === 'dev.queuemein.com' ||
      hostname === 'admin.test.queuemein.com' || hostname === 'api.test.queuemein.com' || hostname === 'test.queuemein.com') {
    return null
  }
  
  // Extract subdomain for different environments
  let subdomain = null
  if (hostname.endsWith('.dev.queuemein.com')) {
    subdomain = hostname.replace('.dev.queuemein.com', '')
  } else if (hostname.endsWith('.test.queuemein.com')) {
    subdomain = hostname.replace('.test.queuemein.com', '')
  } else if (hostname.endsWith('.queuemein.com')) {
    subdomain = hostname.replace('.queuemein.com', '')
  }
  
  // Validate subdomain format
  if (subdomain && /^[a-zA-Z0-9_-]+$/.test(subdomain)) {
    return subdomain
  }
  
  return null
}

function AdminApp() {
  const { user, loading, logout, isSuperAdmin, isImpersonating, impersonationData } = useAuth();
  const { selectedTenantId, tenantChangeCounter, isResolvingSubdomain } = useTenantContext();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedTenant] = useState('All Tenants');
  const [subdomainValid, setSubdomainValid] = useState<boolean | null>(null);
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [configLoaded, setConfigLoaded] = useState(false)
  const subdomainTenant = getSubdomainTenant();
  
  // Ensure tenant subdomains never access tenants tab
  useEffect(() => {
    if (subdomainTenant && activeTab === 'tenants') {
      setActiveTab('dashboard');
    }
  }, [subdomainTenant, activeTab]);
  
  // Validate subdomain and load tenant config
  useEffect(() => {
    if (!subdomainTenant) {
      setSubdomainValid(true); // No subdomain = valid (admin.queuemein.com)
      return;
    }
    
    // Check if subdomain format is valid
    const isValidFormat = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(subdomainTenant) && subdomainTenant.length >= 3;
    
    if (!isValidFormat) {
      // Invalid format - redirect to main site
      window.location.href = 'https://queuemein.com';
      return;
    }
    
    // Only check tenant if we haven't validated yet
    if (subdomainValid === null) {
      // Valid format - check if tenant exists and load its Cognito config
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || (() => {
        const hostname = window.location.hostname
        if (hostname.includes('dev.queuemein.com')) return 'https://api.dev.queuemein.com'
        if (hostname.includes('test.queuemein.com')) return 'https://api.test.queuemein.com'
        return 'https://api.' + 'queuemein.com'
      })()
      fetch(`${apiBaseUrl}/admin/resolve-subdomain?subdomain=${subdomainTenant}`)
        .then(response => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error('Tenant not found');
          }
        })
        .then(data => {
          // Load tenant-specific Cognito config
          return loadTenantCognitoConfig(data.tenant_id);
        })
        .then(() => {
          setSubdomainValid(true);
          setConfigLoaded(true);
          setShowLoginForm(true); // Show login form for valid subdomains
        })
        .catch(() => {
          // Tenant doesn't exist - redirect to main site
          window.location.href = 'https://queuemein.com';
        });
    }
  }, [subdomainTenant, subdomainValid]);
  
  // Determine active tenant: subdomain > impersonation > selection > user tenant
  const activeTenantId = (subdomainTenant && subdomainValid) ? subdomainTenant :
    (isImpersonating ? 
      user?.tenant_from_group || impersonationData?.impersonatedUser?.tenant_id :
      (isSuperAdmin ? selectedTenantId : user?.tenant_from_group));
  

  
  if (loading || (subdomainTenant && subdomainValid === null) || isResolvingSubdomain) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">
          {isResolvingSubdomain ? 'Resolving tenant...' : 'Loading...'}
        </div>
      </div>
    );
  }
  
  // For subdomains, we handle authentication redirect in useEffect above
  // No need for separate invalid subdomain handling here since we redirect immediately
  


  if (!user) {
    // For admin.queuemein.com, show login button instead of auto-redirect
    if (!subdomainTenant) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <div className="text-center space-y-6">
              <div className="space-y-2">
                <h1 className="text-3xl font-bold text-gray-900">QueueMeIn</h1>
                <p className="text-gray-600">Virtual Queue Management</p>
              </div>
              
              <div className="space-y-4">
                <p className="text-lg text-gray-700">Please sign in to continue</p>
                <button 
                  onClick={() => cognitoAuth.login()}
                  className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 shadow-lg"
                >
                  Sign In
                </button>
              </div>
              
              <div className="text-sm text-gray-500">
                Secure authentication powered by AWS Cognito
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // For subdomains, show login button only after config is loaded
    if (showLoginForm && (configLoaded || isTenantConfigLoaded())) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <div className="text-center space-y-6">
              <div className="space-y-2">
                <h1 className="text-3xl font-bold text-gray-900">QueueMeIn</h1>
                <p className="text-gray-600">{subdomainTenant?.toUpperCase()} Portal</p>
              </div>
              
              <div className="space-y-4">
                <p className="text-lg text-gray-700">Please sign in to continue</p>
                <button 
                  onClick={() => cognitoAuth.login()}
                  className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 shadow-lg"
                >
                  Sign In
                </button>
              </div>
              
              <div className="text-sm text-gray-500">
                Secure authentication powered by AWS Cognito
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show loading while validating tenant
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">
          Validating tenant...
        </div>
      </div>
    );
  }
  
  // Determine user role considering impersonation and subdomain
  const getUserRole = (): 'super_admin' | 'tenant_admin' | 'tenant_manager' | 'viewer' => {
    if (isImpersonating) {
      const impRole = impersonationData?.impersonatedUser?.role?.toLowerCase() || 'tenant_admin';
      if (impRole === 'tenant_manager' || impRole === 'manager') return 'tenant_manager';
      if (impRole === 'viewer') return 'viewer';
      return 'tenant_admin';
    }
    
    if (user.groups?.includes('SUPER_ADMIN') && !subdomainTenant) return 'super_admin';
    
    const role = user.role?.toLowerCase() || 'tenant_admin';
    if (role === 'tenant_manager' || role === 'manager') return 'tenant_manager';
    if (role === 'viewer') return 'viewer';
    return 'tenant_admin';
  };
  
  const userRole = getUserRole();

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <Header 
        user={user}
        onLogout={logout}
      />
      
      <ImpersonationBar />
      
      <div className="flex flex-1 overflow-hidden relative">
        <Sidebar 
          activeTab={activeTab}
          onTabChange={setActiveTab}
          userRole={userRole}
          isImpersonating={isImpersonating}
          impersonatedUserRole={impersonationData?.impersonatedUser?.role}
        />
        
        <MainContent 
          key={`main-${activeTenantId || 'no-tenant'}-${tenantChangeCounter}`}
          activeTab={activeTab}
          selectedTenant={selectedTenant}
          userRole={userRole}
          kpiData={mockKPIData}
          onTabChange={setActiveTab}
        />
      </div>
    </div>
  );
}

function App() {
  return (
    <TenantProvider>
      <ThemeProvider>
        <Router>
          <Routes>
            <Route path="/join/:tenantId/:locationId/:serviceId" element={<JoinQueue />} />
            <Route path="/live-dashboard" element={<LiveDashboard />} />
            <Route path="/*" element={<AdminApp />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </TenantProvider>
  );
}

export default App;